import { Component } from 'react';

import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import BpkDialog, {
  HEADER_ICON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-dialog';
import BpkSmallInfoCircleIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/information-circle';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import BpkBreakpoint, { BREAKPOINTS } from '../../common/BpkBreakpoint';
import { withI18n } from '../../contexts/i18n';
import logos from '../logos';

import BigShadow from './BigShadow';
import BigWoman from './BigWoman';

import type { ButtonClick } from '../../types';
import type { I18nService } from '@shared/types/i18n';
import type { ConfidenceProviders } from '@shared/types/utils';

import STYLES from './RefreshModal.module.scss';

import { getApplicationElement } from '@shared/utils-frontend/ApplicationElements';

type Props = {
  i18n: I18nService;
  confidenceProviders: ConfidenceProviders;
  onClick: ButtonClick;
  refreshTime?: number;
  forceRefreshTime?: number;
};

type State = {
  isOpen: boolean;
  isOverTime: boolean;
};

const cls = cssModules(STYLES);

const LONGER_PRICE_REFRESH_TIME = 24 * 60 * 60 * 1000;
const LONGER_FORCE_REFRESH_TIME = 25 * 60 * 60 * 1000;

class RefreshModal extends Component<Props, State> {
  state: State = {
    isOpen: false,
    isOverTime: false,
  };

  timer: any = null;

  expiryTimer: any = null;

  componentDidMount() {
    const { forceRefreshTime, refreshTime } = this.props;

    this.timer = setInterval(() => {
      this.setState({ isOpen: true });
    }, refreshTime || LONGER_PRICE_REFRESH_TIME);

    this.expiryTimer = setTimeout(() => {
      this.setState({ isOverTime: true });
    }, forceRefreshTime || LONGER_FORCE_REFRESH_TIME);
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  onCloseModal = () => {
    this.setState({ isOpen: false });
  };

  render() {
    if (!this.state.isOpen) {
      return null;
    }

    const {
      confidenceProviders,
      i18n: { translate },
      onClick,
    } = this.props;
    const partners = confidenceProviders.slice(0, 6);
    const children = (
      <div className={cls('RefreshModal__textAndButton')}>
        <BpkText textStyle={TEXT_STYLES.heading3} tagName="p">
          {translate('PriceChange_modal_title')}
        </BpkText>
        <BpkText tagName="p">
          {translate('PriceChange_modal_description_refresh')}
        </BpkText>
        <BpkButtonV2 className={cls('RefreshModal__button')} onClick={onClick}>
          <BpkText textStyle={TEXT_STYLES.heading4}>
            {translate('PriceChange_modal_button')}
          </BpkText>
        </BpkButtonV2>
        {!this.state.isOverTime && (
          <BpkButtonV2
            className={cls('RefreshModal__button')}
            onClick={this.onCloseModal}
            type={BUTTON_TYPES.secondary}
          >
            <BpkText textStyle={TEXT_STYLES.heading4}>
              {translate('PriceAlerts_label_successButtonText')}
            </BpkText>
          </BpkButtonV2>
        )}
      </div>
    );

    return (
      <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
        {(isActive: boolean) =>
          isActive ? (
            <BpkDialog
              id="refresh-dialog"
              className={cls('RefreshModal__dialog')}
              isOpen={this.state.isOpen}
              getApplicationElement={getApplicationElement}
              headerIcon={<BpkSmallInfoCircleIcon />}
              headerIconType={HEADER_ICON_TYPES.warning}
              ariaLabel=""
              ariaModal
            >
              {children}
            </BpkDialog>
          ) : (
            <div className={cls('RefreshModal__container')}>
              <div className={cls('RefreshModal__scrim')} />
              <div className={cls('RefreshModal__content')}>
                <div className={cls('RefreshModal__illustrationContainer')}>
                  <div className={cls('RefreshModal__womanContainer')}>
                    <div className={cls('RefreshModal__woman')}>
                      <BigWoman />
                    </div>
                  </div>
                  <div className={cls('RefreshModal__shadowContainer')}>
                    <div className={cls('RefreshModal__shadow')}>
                      <BigShadow />
                    </div>
                  </div>
                  {partners.map((partner, index) => (
                    <div
                      key={partner}
                      className={cls(`RefreshModal__logo${index + 1}`)}
                    >
                      <img
                        src={logos[partner]}
                        className={cls('RefreshModal__logo')}
                        alt="logo"
                      />
                    </div>
                  ))}
                </div>
                <div className={cls('RefreshModal__arrowContainer')}>
                  <div className={cls('RefreshModal__arrow')} />
                </div>
                {children}
              </div>
            </div>
          )
        }
      </BpkBreakpoint>
    );
  }
}

export default withI18n(RefreshModal);
